import { Box, Container, Divider, Link, Paper, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';

import PageWrapper from 'Components/PageWrapper';
import { useConfig } from 'store/state';

// Public SMS legal pages (/sms-terms, /sms-privacy). Carrier toll-free
// verification requires these on the registered business's own website, and
// the entity named here must match the registration's legal company name —
// both come from useConfig, the site's single business-identity source.
export const PROGRAM_NAME = 'Administrative Messaging Program';
export const EFFECTIVE_DATE = 'September 30, 2026';

// Carrier-required disclosure, quoted verbatim on both pages and in the
// toll-free verification registration.
export const CARRIER_DISCLOSURE =
  'Message frequency may vary. Message and data rates may apply. ' +
  'Reply HELP for help or STOP to opt out.';

// "10 Space LLC, doing business as Tenspace Software ("Tenspace," "we," "us")"
export function useLegalIdentity() {
  const [config] = useConfig();
  return {
    entity: config.business_official_name,
    tradeName: config.business_display_name,
    supportEmail: config.contact_email,
    address: config.contact_address,
    phone: config.contact_phone,
    party:
      `${config.business_official_name}, doing business as ${config.business_display_name} ` +
      '("Tenspace," "we," "us")',
  };
}

export function LegalSection({ title, children }) {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        {title}
      </Typography>
      {typeof children === 'string' ? (
        <Typography variant="body1" color="text.secondary">
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  );
}

export function SupportEmailLink() {
  const { supportEmail } = useLegalIdentity();
  return <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>;
}

export function ContactLine() {
  const { entity, address, phone } = useLegalIdentity();
  return (
    <Typography variant="body1" color="text.secondary">
      {entity} · {address} · {phone} · Email: <SupportEmailLink />
    </Typography>
  );
}

export default function LegalPageLayout({ title, crossLink, children }) {
  const { entity, tradeName } = useLegalIdentity();
  return (
    <PageWrapper>
      <Helmet>
        <title>{`${title} | ${tradeName} (${entity})`}</title>
      </Helmet>
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper variant="outlined" sx={{ p: { xs: 3, sm: 6 } }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {entity} (d/b/a {tradeName}) — {PROGRAM_NAME}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Effective date: {EFFECTIVE_DATE}
          </Typography>

          <Paper
            variant="outlined"
            sx={{ p: 2, bgcolor: 'action.hover', borderLeft: 4, borderLeftColor: 'primary.main' }}
          >
            <Typography variant="body2" fontWeight="bold">
              {CARRIER_DISCLOSURE}
            </Typography>
          </Paper>

          {children}

          <Divider sx={{ mt: 6, mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            &copy; 2026 {entity}. All rights reserved. ·{' '}
            <Link component={RouterLink} to={crossLink.to}>
              {crossLink.label}
            </Link>{' '}
            · <SupportEmailLink />
          </Typography>
        </Paper>
      </Container>
    </PageWrapper>
  );
}
